import { useMemo, useState } from "react";
import {
  Loader2,
  Building2,
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Eye,
  EyeOff,
  ImagePlus,
} from "lucide-react";
import { Pagination, pageSlice } from "../components/Pagination";
import {
  useIssuers,
  useCreateIssuer,
  useUpdateIssuer,
  useDeleteIssuer,
  uploadIssuerLogo,
} from "../api/issuers";
import type { Issuer, IssuerFormData } from "../types/issuer";

const EMPTY_FORM: IssuerFormData = {
  nombre: "",
  cif: "",
  direccion: "",
  email: "",
  telefono: "",
  color_hex: "#1F4E79",
  logo: null,
  numero_inicial: "",
  numero_digitos: 4,
  ultimo_numero_factura: 0,
  literatura: "",
  textos_legales_mercantil: "",
  textos_legales_clausulas: "",
  verifacti_api_key: "",
  mail_host: "",
  mail_port: null,
  mail_username: "",
  mail_password: "",
  mail_encryption: "tls",
  mail_from_address: "",
  mail_from_name: "",
};

/** Construye el número de factura que se generará a continuación. */
function previewNumero(prefijo: string, digitos: number, ultimo: number): string {
  const next = (Number.isFinite(ultimo) ? ultimo : 0) + 1;
  const width = Math.max(Number.isFinite(digitos) && digitos > 0 ? digitos : 4, String(next).length);
  return `${prefijo ?? ""}${String(next).padStart(width, "0")}`;
}

export function IssuersListPage() {
  const { data: issuers, isLoading, error } = useIssuers();
  const createMut = useCreateIssuer();
  const updateMut = useUpdateIssuer();
  const deleteMut = useDeleteIssuer();

  const [editing, setEditing] = useState<Issuer | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<IssuerFormData>(EMPTY_FORM);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showMailPassword, setShowMailPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const pagedIssuers = useMemo(
    () => pageSlice(issuers, page, pageSize),
    [issuers, page, pageSize],
  );

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditing(null);
    setCreating(true);
    setShowApiKey(false);
    setFormError(null);
    setLogoPreview(null);
  }

  function openEdit(em: Issuer) {
    setForm({
      nombre: em.nombre,
      cif: em.cif ?? "",
      direccion: em.direccion ?? "",
      email: em.email ?? "",
      telefono: em.telefono ?? "",
      color_hex: em.color_hex || "#1F4E79",
      logo: em.logo ?? null,
      numero_inicial: em.numero_inicial ?? "",
      numero_digitos: em.numero_digitos ?? 4,
      ultimo_numero_factura: em.ultimo_numero_factura ?? 0,
      literatura: em.literatura ?? "",
      textos_legales_mercantil: em.textos_legales_mercantil ?? "",
      textos_legales_clausulas: em.textos_legales_clausulas ?? "",
      // La clave no se devuelve nunca: vacío = mantener la actual.
      verifacti_api_key: "",
      mail_host: em.mail_host ?? "",
      mail_port: em.mail_port ?? null,
      mail_username: em.mail_username ?? "",
      // La contraseña SMTP no se devuelve: vacío = mantener la actual.
      mail_password: "",
      mail_encryption: em.mail_encryption ?? "tls",
      mail_from_address: em.mail_from_address ?? "",
      mail_from_name: em.mail_from_name ?? "",
    });
    setEditing(em);
    setCreating(false);
    setShowApiKey(false);
    setFormError(null);
    setLogoPreview(em.logo_url ?? null);
  }

  async function handleLogoChange(file: File | null) {
    if (!file) return;
    setUploadingLogo(true);
    setFormError(null);
    try {
      const { path, url } = await uploadIssuerLogo(file);
      setForm((f) => ({ ...f, logo: path }));
      setLogoPreview(url);
    } catch {
      setFormError("No se pudo subir el logo. Prueba con otra imagen (máx. 4 MB).");
    } finally {
      setUploadingLogo(false);
    }
  }

  function close() {
    setEditing(null);
    setCreating(false);
    setFormError(null);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    try {
      const payload: IssuerFormData = {
        ...form,
        cif: form.cif || null,
        direccion: form.direccion || null,
        email: form.email || null,
        telefono: form.telefono || null,
        logo: form.logo || null,
        numero_inicial: form.numero_inicial || null,
        numero_digitos: form.numero_digitos ?? 4,
        ultimo_numero_factura: form.ultimo_numero_factura ?? 0,
        literatura: form.literatura || null,
        textos_legales_mercantil: form.textos_legales_mercantil || null,
        textos_legales_clausulas: form.textos_legales_clausulas || null,
        verifacti_api_key: form.verifacti_api_key || null,
        mail_host: form.mail_host || null,
        mail_port: form.mail_port || null,
        mail_username: form.mail_username || null,
        mail_password: form.mail_password || null,
        mail_encryption: form.mail_encryption || null,
        mail_from_address: form.mail_from_address || null,
        mail_from_name: form.mail_from_name || null,
      };
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, payload });
      } else {
        await createMut.mutateAsync(payload);
      }
      close();
    } catch (err: unknown) {
      const errAny = err as {
        response?: { data?: { message?: string; errors?: Record<string, string[]> } };
      };
      const first = errAny?.response?.data?.errors
        ? Object.values(errAny.response.data.errors).flat()[0]
        : null;
      setFormError(first ?? errAny?.response?.data?.message ?? "Error guardando.");
    }
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <Building2 size={28} className="text-brand-700" /> Emisores
          </h1>
          <p className="text-slate-500 mt-1">
            Razones sociales del holding. Cada una factura, gestiona clientes y
            tiene su propia configuración de Verifacti.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 font-bold text-sm shadow-soft transition"
        >
          <Plus size={18} /> Nuevo emisor
        </button>
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-4">
          Error al cargar los emisores.
        </div>
      )}

      {issuers && (
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
          <div className="overflow-auto max-h-[calc(100vh-240px)]">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Nombre</th>
                  <th className="text-left px-4 py-3 font-bold">CIF</th>
                  <th className="text-left px-4 py-3 font-bold">Email</th>
                  <th className="text-left px-4 py-3 font-bold">Próximo nº</th>
                  <th className="text-center px-4 py-3 font-bold">Color</th>
                  <th className="text-center px-4 py-3 font-bold">Verifacti</th>
                  <th className="text-center px-4 py-3 font-bold">Correo</th>
                  <th className="text-right px-4 py-3 font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {issuers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                      Aún no hay emisores.
                    </td>
                  </tr>
                )}
                {pagedIssuers.map((em) => (
                <tr
                  key={em.id}
                  className="border-t border-slate-100 hover:bg-slate-50/60"
                >
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900">{em.nombre}</div>
                    {em.direccion && (
                      <div className="text-xs text-slate-500 truncate max-w-xs">
                        {em.direccion}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm">
                    {em.cif ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {em.email ?? "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm">
                    {previewNumero(
                      em.numero_inicial ?? "",
                      em.numero_digitos ?? 4,
                      em.ultimo_numero_factura ?? 0,
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className="inline-block w-6 h-6 rounded border border-slate-200"
                      style={{ background: em.color_hex }}
                      title={em.color_hex}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    {em.tiene_api_key ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold">
                        Configurado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-bold">
                        Sin API key
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {em.tiene_mail_config ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold">
                        Configurado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-bold">
                        Global
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(em)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              `Eliminar emisor ${em.nombre}? Esta acción puede afectar a facturas y clientes asociados.`,
                            )
                          ) {
                            deleteMut.mutate(em.id);
                          }
                        }}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={issuers.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      )}

      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <form
            onSubmit={save}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6 my-8"
          >
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">
                {editing ? `Editar emisor: ${editing.nombre}` : "Nuevo emisor"}
              </h2>
              <button
                type="button"
                onClick={close}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </header>

            {formError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-sm">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Nombre / Razón social *
                </label>
                <input
                  required
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  CIF / NIF
                </label>
                <input
                  type="text"
                  value={form.cif ?? ""}
                  onChange={(e) => setForm({ ...form, cif: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                />
              </div>

              <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <div className="text-xs font-black text-slate-600 uppercase tracking-wider mb-3">
                  Numeración de facturas
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Prefijo
                    </label>
                    <input
                      type="text"
                      value={form.numero_inicial ?? ""}
                      onChange={(e) =>
                        setForm({ ...form, numero_inicial: e.target.value })
                      }
                      placeholder="ej: BG, PW, INP"
                      className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Dígitos
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={form.numero_digitos ?? 4}
                      onChange={(e) =>
                        setForm({ ...form, numero_digitos: Number(e.target.value) })
                      }
                      className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Próximo número
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={(form.ultimo_numero_factura ?? 0) + 1}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          ultimo_numero_factura: Math.max(0, Number(e.target.value) - 1),
                        })
                      }
                      className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                    />
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  La próxima factura se emitirá como{" "}
                  <span className="font-mono font-bold text-brand-700">
                    {previewNumero(
                      form.numero_inicial ?? "",
                      form.numero_digitos ?? 4,
                      form.ultimo_numero_factura ?? 0,
                    )}
                  </span>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email ?? ""}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Teléfono
                </label>
                <input
                  type="text"
                  value={form.telefono ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, telefono: e.target.value })
                  }
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Dirección
                </label>
                <input
                  type="text"
                  value={form.direccion ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, direccion: e.target.value })
                  }
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Color de marca (PDF)
                </label>
                <div className="flex gap-2 items-center mt-1">
                  <input
                    type="color"
                    value={form.color_hex ?? "#1F4E79"}
                    onChange={(e) =>
                      setForm({ ...form, color_hex: e.target.value })
                    }
                    className="h-10 w-16 rounded border border-slate-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={form.color_hex ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, color_hex: e.target.value })
                    }
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                    placeholder="#1F4E79"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Logo (aparece en el PDF de la factura)
                </label>
                <div className="flex items-center gap-4 mt-1">
                  <div className="h-20 w-40 shrink-0 rounded-lg border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <span className="text-xs text-slate-400">Sin logo</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-slate-300 hover:bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 w-fit">
                      {uploadingLogo ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <ImagePlus size={16} />
                      )}
                      {uploadingLogo ? "Subiendo…" : "Subir imagen"}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        disabled={uploadingLogo}
                        onChange={(e) => handleLogoChange(e.target.files?.[0] ?? null)}
                      />
                    </label>
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setForm((f) => ({ ...f, logo: null }));
                          setLogoPreview(null);
                        }}
                        className="text-xs font-bold text-rose-600 hover:text-rose-800 w-fit"
                      >
                        Quitar logo
                      </button>
                    )}
                    <span className="text-xs text-slate-400">PNG, JPG o WEBP · máx. 4 MB</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  API Key Verifacti
                </label>
                <div className="flex gap-1 mt-1">
                  <input
                    type={showApiKey ? "text" : "password"}
                    value={form.verifacti_api_key ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, verifacti_api_key: e.target.value })
                    }
                    placeholder={
                      editing?.tiene_api_key
                        ? "•••• configurada · escribe para cambiarla"
                        : "Sin clave = no se envía a Verifacti"
                    }
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
                  >
                    {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Literatura (lema / tagline opcional)
                </label>
                <input
                  type="text"
                  value={form.literatura ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, literatura: e.target.value })
                  }
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Textos legales mercantil
                </label>
                <textarea
                  rows={4}
                  value={form.textos_legales_mercantil ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      textos_legales_mercantil: e.target.value,
                    })
                  }
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  placeholder="Datos del registro mercantil…"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Textos legales / cláusulas
                </label>
                <textarea
                  rows={4}
                  value={form.textos_legales_clausulas ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      textos_legales_clausulas: e.target.value,
                    })
                  }
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  placeholder="Cláusulas legales que aparecen al pie del PDF…"
                />
              </div>

              {/* ── Servidor de correo (SMTP) propio del emisor ── */}
              <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <div className="text-xs font-black text-slate-600 uppercase tracking-wider mb-1">
                  Servidor de correo (SMTP)
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Desde aquí se envían las facturas por email a los clientes de
                  este emisor. Si lo dejas vacío, se usará el correo global del
                  sistema.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Servidor SMTP (host)
                    </label>
                    <input
                      type="text"
                      value={form.mail_host ?? ""}
                      onChange={(e) => setForm({ ...form, mail_host: e.target.value })}
                      placeholder="ej: smtp.gmail.com"
                      className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Puerto
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={65535}
                      value={form.mail_port ?? ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          mail_port: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                      placeholder="587"
                      className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Cifrado
                    </label>
                    <select
                      value={form.mail_encryption ?? ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          mail_encryption: (e.target.value || null) as
                            | "tls"
                            | "ssl"
                            | null,
                        })
                      }
                      className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="tls">TLS (587)</option>
                      <option value="ssl">SSL (465)</option>
                      <option value="">Ninguno</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Usuario SMTP
                    </label>
                    <input
                      type="text"
                      value={form.mail_username ?? ""}
                      onChange={(e) =>
                        setForm({ ...form, mail_username: e.target.value })
                      }
                      placeholder="usuario@dominio.com"
                      className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Contraseña SMTP
                    </label>
                    <div className="flex gap-1 mt-1">
                      <input
                        type={showMailPassword ? "text" : "password"}
                        value={form.mail_password ?? ""}
                        onChange={(e) =>
                          setForm({ ...form, mail_password: e.target.value })
                        }
                        placeholder={
                          editing?.tiene_mail_password
                            ? "•••• configurada · escribe para cambiarla"
                            : "Contraseña del correo"
                        }
                        className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowMailPassword(!showMailPassword)}
                        className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
                      >
                        {showMailPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Remitente (email)
                    </label>
                    <input
                      type="email"
                      value={form.mail_from_address ?? ""}
                      onChange={(e) =>
                        setForm({ ...form, mail_from_address: e.target.value })
                      }
                      placeholder="Por defecto: el email del emisor"
                      className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Remitente (nombre)
                    </label>
                    <input
                      type="text"
                      value={form.mail_from_name ?? ""}
                      onChange={(e) =>
                        setForm({ ...form, mail_from_name: e.target.value })
                      }
                      placeholder="Por defecto: el nombre del emisor"
                      className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              {editing && (
                <div className="text-xs text-slate-500 font-mono">
                  Próxima factura:{" "}
                  <span className="font-bold text-brand-700">
                    {previewNumero(
                      form.numero_inicial ?? "",
                      form.numero_digitos ?? 4,
                      form.ultimo_numero_factura ?? 0,
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-end gap-2 ml-auto">
                <button
                  type="button"
                  onClick={close}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMut.isPending || updateMut.isPending}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
                >
                  <Save size={14} /> Guardar emisor
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
